import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import { createStructuredSelector } from 'reselect';
import prop from 'lodash/fp/prop';
import { flow } from 'lodash-es';
import useSimpleForm from 'hooks/useSimpleForm';
import ToastContext from 'contexts/Toast';
import { useMutateMerchant } from './useMerchant';
import { merchantFormSubmitApiError } from 'constants/error';

const useRules = () => {
  return useMemo(() => {
    return {
      preorderPeriod: {
        required: {
          value: true,
          message: {
            key: 'required',
            options: {
              ns: 'fieldValidation',
            },
          },
        },
        pattern: {
          value: /^\d+$/,
          message: {
            key: 'Please enter numbers only',
            options: {
              ns: 'main',
            },
          },
        },
        min: {
          value: 30,
          message: {
            key: 'min',
            options: {
              ns: 'fieldValidation',
              value: 30,
            },
          },
        },
        max: {
          value: 364,
          message: {
            key: 'max',
            options: {
              ns: 'fieldValidation',
              value: 364,
            },
          },
        },
      },
      contentApiToken: {
        required: {
          value: true,
          message: {
            key: 'required',
            options: {
              ns: 'fieldValidation',
            },
          },
        },
      },
    };
  }, []);
};

const merchantSettingSelector = createStructuredSelector({
  preorderPeriod: flow(prop('preorderPeriod'), Number),
  contentApiToken: prop('contentApiToken'),
});

const useMerchantForm = ({ defaultValues }) => {
  const { t } = useTranslation('main');
  const { createUIToast } = useContext(ToastContext);
  const [credentialUploadFieldError, setCredentialUploadFieldError] =
    useState(false);

  const showCredentialUploadAlertToast = (i18nKey) => {
    createUIToast({
      id: nanoid(),
      type: 'alert',
      titleWithParams: {
        key: i18nKey,
      },
      duration: 5000,
    });
  };
  const onSuccess = useCallback(() => {
    createUIToast({
      id: nanoid(),
      type: 'success',
      titleWithParams: {
        key: 'main:Merchant has been saved successfully',
      },
      duration: 5000,
    });
  }, [createUIToast]);
  const onError = useCallback(
    (error) => {
      const errorCode = error.response?.data?.code;
      switch (errorCode) {
        case merchantFormSubmitApiError.INVALID_GMC_ID:
          // error out the credential upload field only when INVALID_GMC_ID error occurs
          setCredentialUploadFieldError(true);
          showCredentialUploadAlertToast('main:Failed to save merchant');
          break;
        case merchantFormSubmitApiError.CAN_NOT_INTEGRATE_WITH_ADVANCED_ACCOUNT:
          showCredentialUploadAlertToast(
            'main:Failed to connect with MCA merchant',
          );
          break;
        case merchantFormSubmitApiError.INVALID_DOMAIN:
          showCredentialUploadAlertToast(
            'main:Failed to connect with invalid domain',
          );
          break;
        default: {
          console.error('Unexpected error:', error);
        }
      }
    },
    [setCredentialUploadFieldError, createUIToast],
  );
  const { mutate, isLoading: submitting } = useMutateMerchant({
    onSuccess,
    onError,
  });
  const patchMerchantSetting = useCallback(
    (merchant) => {
      setCredentialUploadFieldError(false);
      const merchantSetting = merchantSettingSelector(merchant);
      mutate(merchantSetting);
    },
    [mutate],
  );
  const rules = useRules();
  const [values, meta] = useSimpleForm({
    defaultValues,
    rules,
    onSubmit: patchMerchantSetting,
  });

  const renderPreorderPeriodMessages = useMemo(
    () => [
      () => {
        const message = meta.errors.preorderPeriod;
        return t(message.key, {
          ...message.options,
          fieldName: t('Preorder Period'),
        });
      },
    ],
    [meta.errors.preorderPeriod, t],
  );

  const formMeta = useMemo(
    () => ({
      ...meta,
      credentialUploadFieldError,
    }),
    [meta, credentialUploadFieldError],
  );

  return [
    values,
    formMeta,
    {
      submitting,
      renderMessages: {
        preorderPeriod: renderPreorderPeriodMessages,
      },
    },
  ];
};

export default useMerchantForm;
